import type { MetaFunction } from "@remix-run/node"
import type { TeamItemsResponse } from "./get-team-items.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"
import * as ItemsGrid from "../../lib/ui/items-grid.tsx"
import * as OpenGraph from "../../lib/utils/open-graph.ts"

import { each, when } from "@iyk/flow"
import { toSentenceCase } from "@iyk/string"
import { Await, useLoaderData } from "@remix-run/react"
import {
  PointsOnboardingDialog,
  usePointsOnboarding,
} from "../../components/points/points-onboarding.tsx"
import { AsyncItemOwnersSection } from "../../components/profiles/item-owners-section.tsx"
import { ItemsFilter } from "../../components/profiles/items-filter.tsx"
import { ItemsOrder } from "../../components/profiles/items-order.tsx"
import { ProfileLayoutWithBanner } from "../../components/profiles/layout.tsx"
import { ShareDialog } from "../../components/profiles/share-dialog.tsx"
import { AsyncUserOwnedItemIndicator } from "../../components/profiles/user-owned-item.tsx"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { loader } from "./loader.server.ts"

export { loader }

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [{ title: "IYK" }]
  const title = `${data.team.name} | IYK`
  return [
    { title },
    ...OpenGraph.generateProperties({
      title,
      pathname: location.pathname,
      imageUrl: data.team.iconUrl,
      description: data.team.description,
      type: "profile",
      username: data.team.name,
    }),
  ]
}

export default () => {
  const data = useLoaderData<typeof loader>()
  const lang = useLang()
  const iykUser = useIykUser()

  const avatarUrl = data.team.iconUrl || UI.toInitialsSVG(data.team.name[0])

  const [items, setItems] = React.useState<TeamItemsResponse["data"]>(data.teamItems.data)

  const nextPageUrl = React.useRef(data.teamItems.pagination.nextPageUrl)

  async function loadNextPage() {
    if (!nextPageUrl.current) return

    const response = await fetch(nextPageUrl.current)
    if (response.ok) {
      const responseData = (await response.json()) as TeamItemsResponse
      setItems((prev) => [...prev, ...responseData.data])
      nextPageUrl.current = responseData.pagination.nextPageUrl
    } else {
      UI.toast({
        title: "Failed to load more items",
        variant: "destructive",
      })
    }
  }

  const showItemsFilter = data.itemsFilterOptions.options.find(
    (option) => option.value === "shop" && option.count > 0,
  )

  const teamItemsTotalCount =
    data.itemsFilterOptions.options.find((option) => option.value === "all")?.count ?? 0

  // Reset the state when the loader data changes.
  React.useEffect(() => {
    setItems(data.teamItems.data)
    nextPageUrl.current = data.teamItems.pagination.nextPageUrl
  }, [data.teamItems])

  const { showPointsOnboardingDialog } = usePointsOnboarding()

  return (
    <ProfileLayoutWithBanner
      banner={{
        avatarUrl,
        bannerUrl: data.team.coverImageUrl,
      }}
    >
      {when(showPointsOnboardingDialog && data.hasPointsEnabled && iykUser.isSignedIn, () => (
        <PointsOnboardingDialog />
      ))}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <p className="text-xl">{data.team.name}</p>
            <React.Suspense fallback={null}>
              <Await resolve={data.currentUserPointsBalancePromise}>
                {(data) => {
                  const pointsBalance = data?.balance
                  if (!pointsBalance) return null
                  return (
                    <span className="font-departure-mono text-[11px] leading-[14px] uppercase">
                      {pointsBalance} {lang.terms.POINTS}
                    </span>
                  )
                }}
              </Await>
            </React.Suspense>
          </div>
          <ShareDialog avatarUrl={avatarUrl} creatorName={data.team.name} type="creator" />
        </div>
        {when(data.team.description, (description) => (
          <p className="max-w-[580px] text-balance text-sm">{description}</p>
        ))}
        {when(data.team.profileLinks, (profileLinks) => (
          <div className="flex items-center flex-wrap gap-4">
            {each(profileLinks, (profileLink, index) => (
              <UI.Link
                key={`${profileLink.href}-${index}`}
                href={profileLink.href}
                rel="noreferrer"
                target="_blank"
                underline={false}
              >
                <UI.Icon.RoundedLink className="relative bottom-px flex-shrink-0" />
                <span className="line-clamp-1">{profileLink.text}</span>
              </UI.Link>
            ))}
          </div>
        ))}
        <AsyncItemOwnersSection team={data.team} itemOwnersPromise={data.teamItemOwnersPromise} />
      </div>

      <UI.NavList className="mt-8">
        <span className="select-none" aria-current="true">
          {toSentenceCase(lang.terms.ITEM__OTHER)}
        </span>
      </UI.NavList>
      {teamItemsTotalCount === 0 ? (
        <ItemsGrid.EmptyState heading={toSentenceCase(lang.terms.NO_ITEMS)}>
          <p className="text-gray-11 text-balance text-sm">
            {toSentenceCase(lang.terms.NO_ITEMS_DESCRIPTION_FOR_TEAMS)}
          </p>
        </ItemsGrid.EmptyState>
      ) : (
        <>
          <ItemsGrid.Header>
            {showItemsFilter ? (
              <ItemsFilter filterOptions={data.itemsFilterOptions} />
            ) : (
              <ItemsGrid.ItemsCount count={teamItemsTotalCount} />
            )}
            <ItemsOrder orderOptions={data.itemsOrderOptions} />
          </ItemsGrid.Header>
          <ItemsGrid.Grid>
            {each(items, (item, index) => (
              <ItemsGrid.Item
                key={`${item.id}-${index}`}
                to={`/creators/${data.team.urlSlug}/items/${item.id}`}
                mediaUrl={item.imageUrl || ""}
                creator={data.team.name}
                name={item.name}
                shopifyProductData={item.shopifyProductData}
              >
                <AsyncUserOwnedItemIndicator
                  itemId={item.id}
                  currentUserOwnedItemsPromise={data.currentUserOwnedItemsPromise}
                />
              </ItemsGrid.Item>
            ))}
          </ItemsGrid.Grid>
          <ItemsGrid.InfiniteScroll loadNext={loadNextPage} />
        </>
      )}
    </ProfileLayoutWithBanner>
  )
}
